import { useState, useEffect, useRef } from 'react';
import { Calendar, Plus, Trash2, Edit2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Command, CommandGroup, CommandItem } from "@/components/ui/command";
import { useToast } from "@/hooks/use-toast";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { useAuth } from '@/hooks/useAuth';
import { supabase } from "@/lib/supabase";
import { DEFAULT_IMPORTANT_DATE_LABELS } from "@/hooks/useContactData";
import { captureError } from '@/lib/sentry';

interface ImportantDate {
  id: string;
  date: string;
  label?: string;
}

interface ContactImportantDatesProps {
  contactId: string;
}

export function ContactImportantDates({ contactId }: ContactImportantDatesProps) {
  const [dates, setDates] = useState<ImportantDate[]>([]);
  const [, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<Partial<ImportantDate>>({});
  const [newDate, setNewDate] = useState<Partial<ImportantDate>>({});
  const [labelInput, setLabelInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [labelSuggestions, setLabelSuggestions] = useState<string[]>([]);
  const { currentWorkspace } = useWorkspaces();
  const { user } = useAuth();
  const { toast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchDates();
  }, [contactId]);

  useEffect(() => {
    if (labelInput.trim()) {
      fetchLabelSuggestions(labelInput);
    } else {
      setLabelSuggestions([]);
    }
  }, [labelInput]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchDates = async () => {
    try {
      const { data, error } = await supabase
        .from('important_dates')
        .select('*')
        .eq('contact_id', contactId)
        .order('date', { ascending: true });

      if (error) throw error;
      setDates(data || []);
    } catch (error) {
      console.error('Error fetching important dates:', error);
      captureError(error as Error, { feature: 'contact-important-dates-fetch' });
      toast({
        title: "Error",
        description: "Failed to load important dates",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchLabelSuggestions = async (input: string) => {
    try {
      const defaultMatches = DEFAULT_IMPORTANT_DATE_LABELS.filter(label =>
        label.toLowerCase().includes(input.toLowerCase())
      );

      const { data, error } = await supabase
        .from('important_dates')
        .select('label')
        .eq('workspace_id', currentWorkspace?.id)
        .not('label', 'is', null)
        .ilike('label', `%${input}%`);

      if (error) throw error;

      const dbLabels = data.map(d => d.label).filter(Boolean);
      const allSuggestions = [...new Set([...defaultMatches, ...dbLabels])];
      
      setLabelSuggestions(allSuggestions);
    } catch (error) {
      console.error('Error fetching label suggestions:', error);
    }
  };

  const addDate = async () => {
    if (!currentWorkspace || !user || !newDate.date) return;

    try {
      const { error } = await supabase.from('important_dates').insert({
        contact_id: contactId,
        workspace_id: currentWorkspace.id,
        created_by: user.id,
        date: newDate.date,
        label: newDate.label?.trim() || null,
      });

      if (error) throw error;

      setNewDate({});
      setIsAdding(false);
      setLabelInput('');
      fetchDates();
      
      toast({
        description: "Important date added successfully",
      });
    } catch (error) {
      console.error('Error adding important date:', error);
      captureError(error as Error, { feature: 'contacts-contact-important-dates-add-date' });
      toast({
        title: "Error",
        description: "Failed to add important date",
        variant: "destructive",
      });
    }
  };

  const updateDate = async (id: string, updates: Partial<ImportantDate>) => {
    try {
      const { error } = await supabase
        .from('important_dates')
        .update({
          date: updates.date,
          label: updates.label?.trim() || null,
        })
        .eq('id', id);

      if (error) throw error;

      setEditingId(null);
      fetchDates();
      
      toast({
        description: "Important date updated successfully",
      });
    } catch (error) {
      captureError(error as Error, { feature: 'contacts-contact-important-dates-update-date' });
      toast({
        title: "Error",
        description: "Failed to update important date",
        variant: "destructive",
      });
    }
  };

  const deleteDate = async (id: string) => {
    try {
      const { error } = await supabase
        .from('important_dates')
        .delete()
        .eq('id', id);

      if (error) throw error;

      fetchDates();
      
      toast({
        description: "Important date removed successfully",
      });
    } catch (error) {
      captureError(error as Error, { feature: 'contacts-contact-important-dates-delete-date' });
      toast({
        title: "Error",
        description: "Failed to remove important date",
        variant: "destructive",
      });
    }
  };

  return (
    <div>
      {dates.map((date) => (
        <div
          key={date.id}
          className="group flex items-center justify-between py-1 px-2 rounded-md hover:bg-muted/50 transition-colors"
        >
          <div className="flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
            {editingId === date.id ? (
              <div className="space-y-2" onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  updateDate(date.id, editDraft);
                }
              }}>
                <Input
                  type="date"
                  value={editDraft.date || ''}
                  onChange={(e) => setEditDraft(prev => ({ ...prev, date: e.target.value }))}
                  className="h-7 text-xs"
                />
                <Input
                  value={editDraft.label || ''}
                  onChange={(e) => setEditDraft(prev => ({ ...prev, label: e.target.value }))}
                  placeholder="Add label (optional)"
                  className="h-7 text-xs"
                />
              </div>
            ) : (
              <div className="text-xs">
                <span className="text-foreground">
                  {format(parseISO(date.date), 'MMM d, yyyy')}
                </span>
                {date.label && (
                  <span className="ml-1.5 text-muted-foreground">
                    {date.label}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setEditDraft({ date: date.date, label: date.label || '' });
                setEditingId(date.id);
              }}
              className="h-6 w-6"
            >
              <Edit2 className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => deleteDate(date.id)}
              className="h-6 w-6 text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      ))}

      {isAdding ? (
        <div className="space-y-2 p-2" onKeyDown={(e) => {
          if (e.key === 'Enter' && newDate.date) {
            e.preventDefault();
            addDate();
          }
        }}>
          <Input
            type="date"
            value={newDate.date || ''}
            onChange={(e) => setNewDate(prev => ({ ...prev, date: e.target.value }))}
            className="w-full h-7 text-xs"
          />
          <div className="relative">
            <Input
              ref={inputRef}
              value={labelInput}
              onChange={(e) => {
                setLabelInput(e.target.value);
                setNewDate(prev => ({ ...prev, label: e.target.value }));
              }}
              placeholder="Add label (optional)"
              onFocus={() => setShowSuggestions(true)}
              className="h-7 text-xs"
            />
            {showSuggestions && labelSuggestions.length > 0 && (
              <div ref={suggestionsRef}>
                <Command className="absolute z-50 w-full mt-1">
                  <CommandGroup>
                    {labelSuggestions.map((label) => (
                      <CommandItem
                        key={label}
                        onSelect={() => {
                          setLabelInput(label);
                          setNewDate(prev => ({ ...prev, label }));
                          setShowSuggestions(false);
                        }}
                        className="text-xs"
                      >
                        {label}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </Command>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button 
              onClick={addDate}
              disabled={!newDate.date}
              size="sm"
              className="h-7 text-xs"
            >
              Add
            </Button>
            <Button 
              variant="ghost"
              onClick={() => {
                setIsAdding(false);
                setNewDate({});
                setLabelInput('');
                setShowSuggestions(false);
              }}
              size="sm"
              className="h-7 text-xs"
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <Button
          variant="ghost"
          className="w-full justify-start text-muted-foreground hover:text-foreground text-xs h-7"
          onClick={() => {
            setNewDate({ date: format(new Date(), 'yyyy-MM-dd') });
            setIsAdding(true);
          }}
        >
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          Add Important Date
        </Button>
      )}
    </div>
  );
}